import React from 'react';

import { NavigationMapItem, NavigationItemType } from '@lumapps/navigation/types';

import { ExternalLinkNavigationItemModal } from '../NavigationItemModal/ExternalLinkNavigationItemModal';
import { NavigationItemModalProps } from '../NavigationItemModal/NavigationItemModal';
import { PickerNavigationItemModal } from '../NavigationItemModal/PickerNavigationItemModal';
import { SectionNavigationItemModal } from '../NavigationItemModal/SectionNavigationItemModal';
import { SystemPageNavigationItemModal } from '../NavigationItemModal/SystemPageNavigationItemModal';

export interface EditNodeDialogProps {
    /** whether the dialog should be open or not */
    isOpen: boolean;
    /** callback to be executed once the dialog is closed */
    onClose: () => void;
    /** callback to be executed once the node has been edited */
    onEdit: (node: NavigationMapItem) => void;
    /** node to edit */
    nodeToEdit: NavigationMapItem;
    /** current default language */
    defaultLanguage: string;
    /** list of the platform's languages */
    languages: string[];
    /** scope to be used for tracking purposes */
    scope: string;
    /** Override for navigation modals */
    navigationItemModalProps?: Partial<NavigationItemModalProps>;
}

/**
 * Dialog that should be display for kick starting the edition flow for a given
 * navigation item. If displays a different modal depending on the node's type and page type.
 * It renders nothing if the type is not declared
 * @param props - EditNodeDialogProps
 */
export const EditNodeDialog: React.FC<EditNodeDialogProps> = ({
    isOpen,
    onClose,
    onEdit,
    nodeToEdit,
    defaultLanguage,
    languages,
    scope,
    navigationItemModalProps,
}) => {
    const { systemPageReference, type } = nodeToEdit;
    const pageType = nodeToEdit.pageReference?.type;

    switch (type) {
        case NavigationItemType.SECTION:
            return (
                <SectionNavigationItemModal
                    defaultLanguage={defaultLanguage}
                    languages={languages}
                    isOpen={isOpen}
                    navigationItem={nodeToEdit}
                    onClose={onClose}
                    onSave={onEdit}
                    isEdit
                    scope={scope}
                    navigationItemModalProps={navigationItemModalProps}
                />
            );
        case NavigationItemType.LINK:
            return (
                <ExternalLinkNavigationItemModal
                    defaultLanguage={defaultLanguage}
                    languages={languages}
                    isOpen={isOpen}
                    navigationItem={nodeToEdit}
                    onClose={onClose}
                    onSave={onEdit}
                    isEdit
                    scope={scope}
                    navigationItemModalProps={navigationItemModalProps}
                />
            );
        case NavigationItemType.PAGE:
            switch (pageType) {
                case NavigationItemType.COMMUNITY:
                case NavigationItemType.CONTENT:
                case NavigationItemType.ARTICLE:
                    return (
                        <PickerNavigationItemModal
                            defaultLanguage={defaultLanguage}
                            languages={languages}
                            navigationItem={nodeToEdit}
                            onClose={onClose}
                            onSave={onEdit}
                            isOpen={false}
                            isEdit
                            isNavigationItemModalOpen
                            scope={scope}
                            navigationItemModalProps={navigationItemModalProps}
                        />
                    );
                default:
                    return null;
            }
        case NavigationItemType.SYSTEM_PAGE:
            switch (systemPageReference?.type) {
                case NavigationItemType.JOURNEYS:
                case NavigationItemType.PERSONAL_FEED:
                case NavigationItemType.VIDEO_GALLERY:
                    return (
                        <SystemPageNavigationItemModal
                            defaultLanguage={defaultLanguage}
                            languages={languages}
                            navigationItem={nodeToEdit}
                            onClose={onClose}
                            onSave={onEdit}
                            isOpen={isOpen}
                            isEdit
                            scope={scope}
                            navigationItemModalProps={navigationItemModalProps}
                        />
                    );
                default:
                    return null;
            }
        default:
            return null;
    }
};
